import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    CreditCard,
    Loader, Download, Eye,
    RotateCcw,
} from "lucide-react";
import {
    getManagerAvanceInboxRequest,
    managerAvanceDecisionRequest,
    getManagerAvanceHistoryRequest,
    exportAvanceRequestsExcelRequest,
} from "../../../../redux/actions/avanceRequestActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import "../TraitementStyles/TraitementAvances.css";
import "../TraitementStyles/TreatmentMesOrdres.css";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);
    useEffect(() => { setQuery(value || ""); }, [value]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);
    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };
    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input type="text" className="dcInput" style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder} value={query} onChange={handleInputChange} onFocus={() => setOpen(true)} />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (<button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Effacer")}><X size={13} /></button>)}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Afficher la liste")}><ChevronDown size={14} /></button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    {t("Aucun résultat")}
                </div>
            )}
        </div>
    );
};

const TraitementAvancesManager = () => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === "ar" ? "ar-MA" : i18n.language === "en" ? "en-US" : "fr-FR";
    const dispatch = useDispatch();

    // ✅ Redux state adapté pour manager
    const { managerInbox, managerHistory, loading } = useSelector((state) => state.avanceRequests);

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const handleResetFilters = () => { setFilterCollab(""); setFilterType(""); setFilterDate(""); };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const delegation = useTreatmentDelegation({
        endpoint: "/api/avance-requests/manager/treatment-delegation",
        label: "d'avances et prêts",
        onUpdated: () => {
            dispatch(getManagerAvanceInboxRequest());
            dispatch(getManagerAvanceHistoryRequest());
        },
    });

    // ✅ Charger données manager au montage
    useEffect(() => {
        dispatch(getManagerAvanceInboxRequest());
        dispatch(getManagerAvanceHistoryRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getManagerAvanceInboxRequest({ silent: true }));
        dispatch(getManagerAvanceHistoryRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString(locale, {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "Transmis à la RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {t(c.label)}
            </span>
        );
    };

    const getRhStatusBadge = (demande) => {
        const rawStatus = String(demande.status || "").toUpperCase();
        if (demande.managerDecision !== true && rawStatus !== "PENDING_ADMIN") return null;
        const config = {
            PENDING_ADMIN: { label: "RH : en attente", bg: "#d1ecf1", color: "#0c5460" },
            approved: { label: "RH : approuvée", bg: "#d4edda", color: "#155724" },
            rejected: { label: "RH : rejetée", bg: "#f8d7da", color: "#721c24" },
        };
        const key = demande.adminDecision === true
            ? "approved"
            : demande.adminDecision === false
                ? "rejected"
                : demande.status;
        const c = config[key];
        return c ? <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>{t(c.label)}</span> : null;
    };

    const handleApprove = (demandeId) => {
        confirmTreatmentApproval(() => dispatch(managerAvanceDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        })));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(managerAvanceDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    // ✅ Mapper les statuts vers les onglets (manager)
    const mapDemandeToTab = (demande) => {
        if (demande.managerDecision === true) return "approuve";
        if (demande.managerDecision === false) return "rejete";
        if (String(demande.status || "").toUpperCase() === "PENDING_ADMIN") return "approuve";
        if (demande.status === "PENDING_MANAGER") return "enCours";
        return null;
    };

    // ✅ Fusionner inbox + history
    const allDemandes = useMemo(() => {
        return [...(managerInbox || []), ...(managerHistory || [])];
    }, [managerInbox, managerHistory]);

    const getFilteredDemandes = () => {
        if (!allDemandes || allDemandes.length === 0) return [];

        let filtered = allDemandes.filter((d) => mapDemandeToTab(d) === activeTab);

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter((d) =>
                (d.employeeName || "").toLowerCase().includes(val) ||
                (d.employeeMatricule || "").toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((d) => (d.avanceType || "").toLowerCase().includes(val));
        }
        if (filterDate) {
            filtered = filtered.filter((d) => {
                if (!d.createdAt) return false;
                return new Date(d.createdAt).toISOString().split('T')[0] === filterDate;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        if (!allDemandes) return [
            { id: "enCours", label: "En attente", count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete", label: "Rejetées", count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En attente",
                count: allDemandes.filter((d) => mapDemandeToTab(d) === "enCours").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: allDemandes.filter((d) => mapDemandeToTab(d) === "approuve").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: allDemandes.filter((d) => mapDemandeToTab(d) === "rejete").length
            },
        ];
    }, [allDemandes]);

    const collaborateurOptions = useMemo(() => {
        if (!allDemandes) return [];
        const map = new Map();
        allDemandes.forEach((d) => {
            const name = d.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: d.employeeMatricule ? `${name} (${d.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [allDemandes]);

    const typeOptions = useMemo(() => {
        if (!allDemandes) return [];
        return [...new Set(allDemandes.map((d) => d.avanceType).filter(Boolean))]
            .sort().map((t) => ({ value: t, label: t }));
    }, [allDemandes]);

    const filtered = getFilteredDemandes();

    if (loading && allDemandes.length === 0) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>{t("Traitement des demandes d'avances et prêts")}</h1>
                        <p>{t("Validez les demandes d'avances sur salaire et prêts de vos collaborateurs")}</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">{t("Délégation Admin RH")}</div>
                            <div className="tavDelegationSetting__sub">{t("Transmettre mon traitement d'avances à l'admin RH")}</div>
                        </div>
                        <label className="tavSwitch" title={t("Délégation Admin RH")}>
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading || !delegation.loaded}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="tavCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Collaborateur / Matricule")}</label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder={t("Nom ou matricule...")} />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Type d'avance")}</label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeOptions} placeholder={t("Rechercher un type...")} />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Date de demande")}</label>
                            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> {t("Réinitialiser")}
                            </button>
                            <button type="button" className="dcBtn dcBtn--outline" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }}
                                onClick={() => dispatch(exportAvanceRequestsExcelRequest("manager_inbox"))}>
                                <Download size={14} /> {t("Exporter Excel")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {t(tab.label)}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>{t("Aucune demande trouvée")}</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>{t("Matricule")}</th><th>{t("Collaborateur")}</th><th>{t("Type")}</th><th>{t("Montant demandé")}</th><th>{t("Remboursement")}</th><th>{t("Date demande")}</th>
                                    {activeTab === "approuve" && <th>{t("Statut RH")}</th>}
                                    {activeTab === "rejete" && <th>{t("Motif")}</th>}
                                    <th className="is-right">{t("Actions")}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tavRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? t("Demande urgente") : ""}
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tavName">
                                                <span className="tavPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tavFire">🔥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavType">
                                                <span className="tavDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <div>
                                                    <div className="tavPrimary">{demande.avanceType}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavAmount">
                                                <div className="tavAmountMain">{formatMontant(demande.montantDemande)} dhs</div>
                                                {demande.tauxInteret > 0 && (
                                                    <div className="tavSub">{t("Taux")} : {demande.tauxInteret}%</div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavPrimary">{demande.nombreMensualites} {t("mois")}</div>
                                            <div className="tavSub">{formatMontant(demande.montantMensuel)} dhs/{t("mois")}</div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        {activeTab === "approuve" && <td>{getRhStatusBadge(demande) || "—"}</td>}

                                        {activeTab === "rejete" && (
                                            <td>
                                                <span className="tavMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {demande.managerComment || demande.adminComment || "—"}
                                                </span>
                                            </td>
                                        )}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tavRowActions">
                                                {demande.status === "PENDING_MANAGER" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tavActionBtn tavActionBtn--ok"
                                                            onClick={() => handleApprove(demande.id)}
                                                            title={t("Approuver")}
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tavActionBtn tavActionBtn--no"
                                                            onClick={() => handleReject(demande)}
                                                            title={t("Refuser")}
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tavActionBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title={t("Voir les détails")}
                                                    style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL - identique à la version admin */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">{t("Détails de la demande")} #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">{t("Demande du")} {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeDetails} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Infos collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> {t("Informations collaborateur")}
                                    </div>

                                    <div className="tavKv">
                                        <span>{t("Matricule")}</span>
                                        <b>{selectedDemande.employeeMatricule || "N/A"}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Nom")}</span>
                                        <b>{selectedDemande.employeeName}</b>
                                    </div>
                                </div>

                                {/* Détails demande */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <CreditCard size={16} /> {t("Détails de la demande")}
                                    </div>

                                    <div className="tavKv">
                                        <span>{t("Type")}</span>
                                        <b>{selectedDemande.avanceType}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Montant demandé")}</span>
                                        <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Mensualités")}</span>
                                        <b>{selectedDemande.nombreMensualites} {t("mois")}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Taux d'intérêt")}</span>
                                        <b>{selectedDemande.tauxInteret || 0}%</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Mensualité")}</span>
                                        <b>{formatMontant(selectedDemande.montantMensuel)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>{t("Total à rembourser")}</span>
                                        <b>{formatMontant(selectedDemande.montantTotal)} dhs</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> {t("Motif de la demande")}
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || " - "}</div>
                                </div>

                                {/* Justificatifs */}
                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <FileText size={16} /> {t("Justificatifs fournis")}
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.justificatifs.map((doc, idx) => (
                                                <span key={idx} className="tavTag">
                                                    {doc.split('/').pop()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Progression du remboursement */}
                                {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement !== null && (
                                    <>
                                        <div className="tavPanel tavPanel--full">
                                            <div className="tavPanel__title">
                                                <Check size={16} /> {t("Progression du remboursement")}
                                            </div>
                                            <div className="tavMiniProgressTop">
                                                <span>
                                                    {t("Payées")} : {selectedDemande.mensualitesPayees}/{selectedDemande.nombreMensualites}
                                                </span>
                                                <span className="tavAccent" style={{color: "#ef4444"}}>
                                                    {t("Restant")} : {formatMontant(selectedDemande.montantTotal - (selectedDemande.montantRembourse || 0))} {t("dhs")}
                                                </span>
                                            </div>
                                            <div className="tavProgressBar">
                                                <div
                                                    className="tavProgressFill"
                                                    style={{
                                                        width: `${(selectedDemande.mensualitesPayees / selectedDemande.nombreMensualites) * 100}%`
                                                    }}
                                                />
                                            </div>
                                            {selectedDemande.dateDebutRemboursement && (
                                                <div className="tavHelp">
                                                    {t("Début remboursement")} : {formatDate(selectedDemande.dateDebutRemboursement)}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tavModal__footer">
                            {selectedDemande.status === "PENDING_MANAGER" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedDemande);
                                        }}
                                        disabled={loading}
                                    >
                                        {t("Refuser")}
                                    </button>

                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedDemande.id);
                                            closeDetails();
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : t("Approuver")}
                                    </button>

                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        {t("Fermer")}
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                    {t("Fermer")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL - identique */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">{t("Refuser la demande d'avance/prêt")}</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.employeeName} — {selectedDemande.avanceType}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeReject} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">{t("Montant demandé")}</span>
                                    <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">{t("Remboursement")}</span>
                                    <b>
                                        {selectedDemande.nombreMensualites} {t("mois")} × {formatMontant(selectedDemande.montantMensuel)} dhs
                                    </b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                {t("Motif du rejet")} <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder={t("Veuillez préciser le motif du rejet de cette demande...")}
                                className="tavTextarea"
                            />
                            <div className="tavHelp">{t("Ce motif sera communiqué au collaborateur")}</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                {t("Annuler")}
                            </button>

                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : t("Confirmer le rejet")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementAvancesManager;
